/**
 * Authentication Guard
 * Client-side route protection using existing UserBar token management
 *
 * Features:
 * - Pre-load authentication checks
 * - Role-based access control
 * - Automatic redirects for unauthorized access
 * - Integration with existing JWT token utilities
 *
 * @version 1.0.0
 * @author Zubduq
 */

/**
 * Authentication Guard Class
 */
class AuthGuard {
  constructor() {
    this.currentUser = null;
    this.isAuthenticated = false;
  }

  /**
   * Check if JWT token is valid (reusing UserBar logic)
   */
  isValidToken(token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp && payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current user from token (reusing UserBar logic)
   */
  getCurrentUser() {
    const token = localStorage.getItem("jwtToken");

    if (!token) {
      console.log('AuthGuard: No token found in localStorage');
      return null;
    }

    if (!this.isValidToken(token)) {
      console.log('AuthGuard: Token is invalid or expired');
      localStorage.removeItem("jwtToken");
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      console.log('AuthGuard: Token payload decoded:', {
        role: payload.role,
        type: payload.type,
        isPublicUser: payload.isPublicUser,
        email: payload.email,
        name: payload.name
      });

      return {
        name: payload.name || payload.email,
        role: payload.role,
        email: payload.email,
        organizationId: payload.organizationId,
        isPublicUser: payload.isPublicUser || false,
        type: payload.type || null,
        userId: payload.userId
      };
    } catch (error) {
      console.error('AuthGuard: Failed to decode token:', error);
      localStorage.removeItem("jwtToken");
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isUserAuthenticated() {
    this.currentUser = this.getCurrentUser();
    this.isAuthenticated = !!this.currentUser;

    // Debug logging
    const token = localStorage.getItem("jwtToken");
    console.log('AuthGuard: Checking authentication...', {
      hasToken: !!token,
      tokenLength: token ? token.length : 0,
      currentUser: this.currentUser,
      isAuthenticated: this.isAuthenticated
    });

    if (token && !this.currentUser) {
      console.error('AuthGuard: Token exists but failed to parse user data. Token preview:', token.substring(0, 50));
    }

    return this.isAuthenticated;
  }

  /**
   * Check if user has required role
   */
  hasRole(requiredRole) {
    console.log('AuthGuard: hasRole check - required:', requiredRole);

    if (!this.isUserAuthenticated()) {
      console.log('AuthGuard: User not authenticated');
      return false;
    }

    console.log('AuthGuard: Current user role:', this.currentUser.role);

    // Admin can access everything
    if (this.currentUser.role === 'admin') {
      console.log('AuthGuard: Admin access granted');
      return true;
    }

    const hasAccess = this.currentUser.role === requiredRole;
    console.log('AuthGuard: Role check result:', hasAccess);
    return hasAccess;
  }

  /**
   * Check if user has any of the required roles
   */
  hasAnyRole(requiredRoles) {
    if (!this.isUserAuthenticated()) return false;

    // Admin can access everything
    if (this.currentUser.role === 'admin') return true;

    return requiredRoles.includes(this.currentUser.role);
  }

  /**
   * Guard admin routes
   */
  requireAdmin() {
    console.log('AuthGuard: Checking admin access...');
    if (!this.hasRole('admin')) {
      console.log('AuthGuard: Admin access denied, redirecting to login');
      this.redirectToLogin();
      return false;
    }
    console.log('AuthGuard: Admin access granted');
    return true;
  }

  /**
   * Guard employee routes (employee, user, or admin)
   */
  requireEmployee() {
    if (!this.hasAnyRole(['employee', 'admin'])) {
      this.redirectToLogin();
      return false;
    }
    return true;
  }

  /**
   * Guard customer routes (customer or admin)
   */
  requireCustomer() {
    if (!this.hasAnyRole(['customer', 'admin'])) {
      this.redirectToLogin();
      return false;
    }
    return true;
  }

  /**
   * Guard authenticated routes (any authenticated user)
   */
  requireAuthentication() {
    if (!this.isUserAuthenticated()) {
      this.redirectToLogin();
      return false;
    }
    return true;
  }

  /**
   * Guard public user routes (for appointment booking)
   */
  requirePublicUser() {
    console.log('AuthGuard: Checking public user requirement...');

    if (!this.isUserAuthenticated()) {
      console.log('AuthGuard: User not authenticated, redirecting to public login');
      this.redirectToPublicLogin();
      return false;
    }

    // Check if user is a public user (from social login)
    // Public users have isPublicUser flag OR role === 'public'
    const isPublic = this.currentUser.isPublicUser === true ||
      this.currentUser.role === 'public' ||
      this.currentUser.type === 'public_user';

    console.log('AuthGuard: Public user check:', {
      isPublicUser: this.currentUser.isPublicUser,
      role: this.currentUser.role,
      type: this.currentUser.type,
      isPublic
    });

    if (!isPublic) {
      console.log('AuthGuard: Not a public user, redirecting to public login');
      this.redirectToPublicLogin();
      return false;
    }

    console.log('AuthGuard: Public user access granted');
    return true;
  }

  /**
   * Redirect to login page
   */
  redirectToLogin() {
    window.location.href = '/auth/login.html';
  }

  /**
   * Redirect to public login page
   */
  redirectToPublicLogin() {
    window.location.href = '/auth/public-login.html';
  }

  /**
   * Check authentication before loading page modules
   */
  async checkAuthBeforeLoad(config = {}) {
    const {
      requiresAuth = false,
      requiredRole = null,
      requiredRoles = [],
      requiresPublicUser = false,
      onSuccess = null,
      onFailure = null
    } = config;

    console.log('AuthGuard: checkAuthBeforeLoad called with config:', config);
    console.log('AuthGuard: Current localStorage token:', !!localStorage.getItem('jwtToken'));

    try {
      let isAuthorized = true;

      if (requiresPublicUser) {
        console.log('AuthGuard: Checking public user requirement...');
        isAuthorized = this.requirePublicUser();
      } else if (requiredRole) {
        console.log('AuthGuard: Checking required role:', requiredRole);
        isAuthorized = this.hasRole(requiredRole);
        if (!isAuthorized) {
          console.log('AuthGuard: Role check failed, redirecting to login');
          this.redirectToLogin();
        }
      } else if (requiredRoles.length > 0) {
        console.log('AuthGuard: Checking required roles:', requiredRoles);
        isAuthorized = this.hasAnyRole(requiredRoles);
        if (!isAuthorized) {
          console.log('AuthGuard: Roles check failed, redirecting to login');
          this.redirectToLogin();
        }
      } else if (requiresAuth) {
        console.log('AuthGuard: Checking authentication requirement...');
        isAuthorized = this.requireAuthentication();
      }

      console.log('AuthGuard: Final authorization result:', isAuthorized);

      if (isAuthorized) {
        console.log('AuthGuard: Authorization successful, calling onSuccess');
        if (onSuccess) await onSuccess(this.currentUser);
        return true;
      } else {
        console.log('AuthGuard: Authorization failed, calling onFailure');
        if (onFailure) await onFailure();
        return false;
      }
    } catch (error) {
      console.error('Auth guard error:', error);
      this.redirectToLogin();
      return false;
    }
  }

  /**
   * Initialize auth guard for specific page types
   */
  static async initForPage(pageType, onSuccess = null) {
    const guard = new AuthGuard();

    const configs = {
      admin: {
        requiresAuth: true,
        requiredRole: 'admin',
        onSuccess
      },
      employee: {
        requiresAuth: true,
        requiredRoles: ['employee', 'admin'],
        onSuccess
      },
      customer: {
        requiresAuth: true,
        requiredRoles: ['customer', 'admin'],
        onSuccess
      },
      authenticated: {
        requiresAuth: true,
        onSuccess
      },
      public: {
        requiresPublicUser: true,
        onSuccess
      }
    };

    const config = configs[pageType];
    if (!config) {
      console.error(`Unknown page type: ${pageType}`);
      return false;
    }

    return await guard.checkAuthBeforeLoad(config);
  }
}

// Export for ES6 modules
export default AuthGuard;

// Make available globally for backward compatibility
if (typeof window !== 'undefined') {
  window.AuthGuard = AuthGuard;
}
